import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, Card, Text, Title, Group, Button, CopyButton, Skeleton } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert } from '@repletoryreach/sdk-ui/data-display';
import { admin } from '@repletoryreach/sdk-api';
import { useT } from '../../../i18n/I18nProvider';
import './backup.css';

interface Step {
  titleKey: string;
  bodyKey: string;
  command: string;
}

/*
 * Each step is one catalogue key plus the command it runs. The page used to
 * assemble every sentence from a `…Before` fragment, a `<code>` and an
 * `…After` fragment, which is how two of those fragments ended up holding the
 * literal string "bodySmall" and printing it into the disaster-recovery
 * procedure. One key per sentence makes that class of bug impossible.
 */
const STEPS: Step[] = [
  {
    titleKey: 'settings.backup.steps.snapshot.title',
    bodyKey: 'settings.backup.steps.snapshot.body',
    command: './scripts/dr/backup.sh'
  },
  {
    titleKey: 'settings.backup.steps.verify.title',
    bodyKey: 'settings.backup.steps.verify.body',
    command: 'cat backups/<timestamp>/manifest.json' // i18n-ignore: shell command, not prose
  },
  {
    titleKey: 'settings.backup.steps.restore.title',
    bodyKey: 'settings.backup.steps.restore.body',
    command: './scripts/dr/restore.sh backups/<timestamp>' // i18n-ignore: shell command, not prose
  },
  {
    titleKey: 'settings.backup.steps.reconcile.title',
    bodyKey: 'settings.backup.steps.reconcile.body',
    command: './scripts/dr/reconcile-ledger.sh'
  },
  {
    titleKey: 'settings.backup.steps.drill.title',
    bodyKey: 'settings.backup.steps.drill.body',
    command: 'bash scripts/dr/drill.sh' // i18n-ignore: shell command, not prose
  }
];

export default function BackupRestorePage() {
  const t = useT();

  /*
   * `GET /v1/admin/ops/backups` answers with the policy the service itself
   * holds: a notice and the two commands it names. `data` is always empty,
   * which is why this page shows no last-run time and no snapshot table: the
   * service records no history, and a card reading "Today, 08:14" would be
   * invented. Reading the policy from the API rather than restating it in the
   * catalogue is what stops this page and System → Backups drifting apart.
   */
  const policyQ = useQuery({
    queryKey: ['admin', 'ops', 'backups'],
    queryFn: () => admin.listBackups()
  });
  const meta = policyQ.data?.meta;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('common.settings')}
        title={t('settings.backup.title')}
        description={t('settings.backup.description')}
      />

      <Alert color="info" title={t('settings.backup.local.title')}>
        {t('settings.backup.local.body')}
      </Alert>

      <div className="rr-backup-grid">
        <Card withBorder>
          <Stack gap="md">
            <Text role="body" fw={600}>{t('settings.backup.procedure.title')}</Text>
            <ol className="rr-backup-steps">
              {STEPS.map((step) => (
                <li key={step.titleKey}>
                  <Stack gap="xs">
                    <Title order={6}>{t(step.titleKey)}</Title>
                    <Text role="bodySmall" c="var(--rr-color-text-muted)">{t(step.bodyKey)}</Text>
                    {/* A runbook exists to get these commands into a terminal
                        correctly, so each one is one click away from the
                        clipboard rather than a hand-retyped path. */}
                    <Group gap="sm" align="center" wrap="nowrap">
                      <code className="rr-backup-command">{step.command}</code>
                      <CopyButton value={step.command}>
                        {({ copied, failed, copy }) => (
                          <Button size="xs" variant="subtle" onClick={copy}>
                            {failed ? t('settings.secret.copyFailed') : copied ? t('common.copied') : t('action.copy')}
                          </Button>
                        )}
                      </CopyButton>
                    </Group>
                  </Stack>
                </li>
              ))}
            </ol>
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('settings.backup.procedure.note')}
            </Text>
          </Stack>
        </Card>

        <Card withBorder>
          <Stack gap="md">
            <Stack gap="xs">
              <Title order={5}>{t('settings.backup.service.title')}</Title>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.backup.service.subtitle')}</Text>
            </Stack>

            {policyQ.isLoading && <Skeleton height={140} />}

            {policyQ.error && (
              <Alert color="error">
                <Group justify="space-between" align="center" wrap="wrap">
                  <Text role="bodySmall">{t('settings.backup.service.loadError')}</Text>
                  <Button size="xs" variant="outline" onClick={() => { void policyQ.refetch(); }}>{t('action.retry')}</Button>
                </Group>
              </Alert>
            )}

            {!policyQ.isLoading && !policyQ.error && (
              <Stack gap="md">
                {meta?.notice && <Text role="bodySmall">{meta.notice}</Text>}
                {meta?.command && <ServiceCommand label={t('settings.backup.service.command')} command={meta.command} />}
                {meta?.restore && <ServiceCommand label={t('settings.backup.service.restore')} command={meta.restore} />}
                {/* The old copy sent operators to System → Backups "for the
                    most recent snapshot timestamps". That page has none
                    either, for the same reason. */}
                <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.backup.service.noHistory')}</Text>
                <Link to="/system/backups" className="rr-backup-link">
                  {t('settings.backup.service.viewSystem')} <span aria-hidden>→</span>
                </Link>
              </Stack>
            )}
          </Stack>
        </Card>
      </div>

      <Alert color="warning" title={t('settings.backup.drill.title')}>
        {t('settings.backup.drill.body')}
      </Alert>
    </Stack>
  );
}

/** A command the service named, with the same copy affordance as the runbook steps. */
function ServiceCommand({ label, command }: { label: string; command: string }) {
  const t = useT();
  return (
    <Stack gap="xs">
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      <Group gap="sm" align="center" wrap="nowrap">
        <code className="rr-backup-command">{command}</code>
        <CopyButton value={command}>
          {({ copied, failed, copy }) => (
            <Button size="xs" variant="subtle" onClick={copy}>
              {failed ? t('settings.secret.copyFailed') : copied ? t('common.copied') : t('action.copy')}
            </Button>
          )}
        </CopyButton>
      </Group>
    </Stack>
  );
}
